/**
 * Converts an extraction field value into pdf-engine search queries.
 *
 * The pdf-engine tokenizes text using Character.isLetterOrDigit(), which is
 * equivalent to our regex [\p{L}\p{M}\p{N}_]+. Apostrophes, hyphens, commas,
 * etc. are all token separators.
 *
 * Returns an array of queries to try in order (primary + fallback).
 * - Primary: all words (≥2 chars) with small gaps for filtered short tokens
 * - Fallback: first few distinctive words only (in case primary is too strict)
 *
 * @param {string} text - The extraction field value
 * @returns {string[]|null} An array of pdf-engine query strings, or null if no usable words
 */
export function buildExtractionSearchQueries(text) {
  if (!text || typeof text !== 'string') return null;

  const allTokens = text.match(/[\p{L}\p{M}\p{N}_]+/gu);
  if (!allTokens || allTokens.length === 0) return null;

  const MIN_WORD_LENGTH = 2;

  const words = [];
  for (let i = 0; i < allTokens.length; i++) {
    if (allTokens[i].length >= MIN_WORD_LENGTH) {
      words.push({ text: allTokens[i], pos: i });
    }
  }

  if (words.length === 0) return null;
  if (words.length === 1) return [words[0].text];

  const queries = [];

  // Primary: all distinctive words with small gaps
  queries.push(buildQuery(words));

  // Fallback: first 8 words only (less strict, for when primary fails)
  const fallbackCount = Math.min(8, words.length);
  const fallback = words.slice(0, fallbackCount);
  const fallbackQuery = buildQuery(fallback);
  if (fallbackQuery !== queries[0]) {
    queries.push(fallbackQuery);
  }

  return queries;
}

/**
 * Backwards-compatible single-query version.
 * @param {string} text
 * @returns {string|null}
 */
export function buildExtractionSearchQuery(text) {
  const queries = buildExtractionSearchQueries(text);
  return queries ? queries[0] : null;
}

function buildQuery(words) {
  const GAP_PADDING = 2;
  const parts = [words[0].text];

  for (let i = 1; i < words.length; i++) {
    const distance = words[i].pos - words[i - 1].pos - 1;
    if (distance > 0) {
      parts.push(`*{0,${distance + GAP_PADDING}} ${words[i].text}`);
    } else {
      parts.push(words[i].text);
    }
  }

  return parts.join(' ');
}
